import { AnyAction, Dispatch } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { fetchApi } from "../../common/api";
import { State } from "../state";
import { GlobalChatConversation, GlobalChatMessage } from "./types";

export enum GLOBAL_CHAT_ACTION {
  LIST_CONVERSATIONS = "GLOBAL_CHAT_LIST_CONVERSATIONS",
  UPSERT_CONVERSATION = "GLOBAL_CHAT_UPSERT_CONVERSATION",
  REPLACE_CONVERSATION = "GLOBAL_CHAT_REPLACE_CONVERSATION",
  SET_CONVERSATION_LOADING = "GLOBAL_CHAT_SET_CONVERSATION_LOADING",
  DELETE_CONVERSATION = "GLOBAL_CHAT_DELETE_CONVERSATION",
  LIST_MESSAGES = "GLOBAL_CHAT_LIST_MESSAGES",
  ADD_MESSAGE = "GLOBAL_CHAT_ADD_MESSAGE",
  REPLACE_MESSAGE = "GLOBAL_CHAT_REPLACE_MESSAGE",
}

export interface ListGlobalChatConversationsAction {
  type: GLOBAL_CHAT_ACTION.LIST_CONVERSATIONS;
  conversations: GlobalChatConversation[];
}

export const listGlobalChatConversationsAction = () => {
  return async (
    dispatch: ThunkDispatch<State, unknown, AnyAction>,
    getState: () => State,
  ) => {
    if (getState().globalChat.hasLoadedConversations) {
      return Object.values(getState().globalChat.conversations);
    }

    const res = await fetchApi<{ conversations: GlobalChatConversation[] }>(
      "/conversations/list",
      "POST",
    );

    dispatch({
      type: GLOBAL_CHAT_ACTION.LIST_CONVERSATIONS,
      conversations: res.conversations ?? [],
    });

    return res.conversations ?? [];
  };
};

export interface UpsertGlobalChatConversationAction {
  type: GLOBAL_CHAT_ACTION.UPSERT_CONVERSATION;
  conversation: GlobalChatConversation;
}

export interface ReplaceGlobalChatConversationAction {
  type: GLOBAL_CHAT_ACTION.REPLACE_CONVERSATION;
  localConversationId: string;
  conversation: GlobalChatConversation;
}

export interface SetGlobalChatConversationLoadingAction {
  type: GLOBAL_CHAT_ACTION.SET_CONVERSATION_LOADING;
  conversationId: string;
  isLoading: boolean;
}

export const createLocalGlobalChatConversationAction = (userId: string) => {
  return (
    dispatch: Dispatch<
      | UpsertGlobalChatConversationAction
      | SetGlobalChatConversationLoadingAction
    >,
  ): GlobalChatConversation => {
    const now = new Date().toISOString();
    const conversation: GlobalChatConversation = {
      id: `local_chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      user_id: userId,
      title: "New chat",
      created_at: now,
      updated_at: now,
    };

    dispatch({
      type: GLOBAL_CHAT_ACTION.UPSERT_CONVERSATION,
      conversation,
    });
    dispatch({
      type: GLOBAL_CHAT_ACTION.SET_CONVERSATION_LOADING,
      conversationId: conversation.id,
      isLoading: true,
    });

    return conversation;
  };
};

export const persistGlobalChatConversationAction = (
  localConversationId: string,
) => {
  return async (
    dispatch: Dispatch<ReplaceGlobalChatConversationAction>,
  ): Promise<GlobalChatConversation> => {
    const res = await fetchApi<{ conversation: GlobalChatConversation }>(
      "/conversations/create",
      "POST",
      {},
    );

    dispatch({
      type: GLOBAL_CHAT_ACTION.REPLACE_CONVERSATION,
      localConversationId,
      conversation: res.conversation,
    });

    return res.conversation;
  };
};

export const generateGlobalChatConversationTitleAction = (
  conversationId: string,
  firstMessage: string,
) => {
  return async (
    dispatch: Dispatch<UpsertGlobalChatConversationAction>,
  ): Promise<GlobalChatConversation> => {
    const res = await fetchApi<{ conversation: GlobalChatConversation }>(
      "/conversations/generate_title",
      "POST",
      {
        conversation_id: conversationId,
        first_message: firstMessage,
      },
    );

    dispatch({
      type: GLOBAL_CHAT_ACTION.UPSERT_CONVERSATION,
      conversation: res.conversation,
    });

    return res.conversation;
  };
};

export interface DeleteGlobalChatConversationAction {
  type: GLOBAL_CHAT_ACTION.DELETE_CONVERSATION;
  conversationId: string;
}

export const deleteGlobalChatConversationAction = (conversationId: string) => {
  return async (dispatch: Dispatch<DeleteGlobalChatConversationAction>) => {
    await fetchApi("/conversations/delete", "POST", {
      conversation_id: conversationId,
    });

    dispatch({
      type: GLOBAL_CHAT_ACTION.DELETE_CONVERSATION,
      conversationId,
    });
  };
};

export interface ListGlobalChatMessagesAction {
  type: GLOBAL_CHAT_ACTION.LIST_MESSAGES;
  conversationId: string;
  messages: GlobalChatMessage[];
}

export const listGlobalChatMessagesAction = (conversationId: string) => {
  return async (
    dispatch: ThunkDispatch<State, unknown, AnyAction>,
    getState: () => State,
  ) => {
    if (conversationId in getState().globalChat.messagesByConversationId) {
      return;
    }

    const res = await fetchApi<{ messages: GlobalChatMessage[] }>(
      "/conversations/list_chat_messages",
      "POST",
      { conversation_id: conversationId },
    );

    dispatch({
      type: GLOBAL_CHAT_ACTION.LIST_MESSAGES,
      conversationId,
      messages: res.messages ?? [],
    });
  };
};

export interface AddGlobalChatMessageAction {
  type: GLOBAL_CHAT_ACTION.ADD_MESSAGE;
  conversationId: string;
  message: GlobalChatMessage;
}

export const addOptimisticGlobalChatMessageAction = (
  conversationId: string,
  text: string,
  userId: string,
) => {
  return (dispatch: Dispatch<AddGlobalChatMessageAction>): string => {
    const tempId = `temp_user_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    dispatch({
      type: GLOBAL_CHAT_ACTION.ADD_MESSAGE,
      conversationId,
      message: {
        id: tempId,
        user_id: userId,
        conversation_id: conversationId,
        from: "user",
        message: text,
        referenced_memory_ids: [],
        created_at: new Date().toISOString(),
      },
    });
    return tempId;
  };
};

export interface ReplaceGlobalChatMessageAction {
  type: GLOBAL_CHAT_ACTION.REPLACE_MESSAGE;
  conversationId: string;
  tempId: string;
  message: GlobalChatMessage;
}

export type SendGlobalChatMessageResult = {
  conversation: GlobalChatConversation;
  user_message: GlobalChatMessage;
  ai_message: GlobalChatMessage;
  referenced_memory_ids: string[];
};

export const sendGlobalChatMessageAction = (
  conversationId: string,
  text: string,
  optimisticMessageId?: string,
) => {
  return async (
    dispatch: Dispatch<
      | AddGlobalChatMessageAction
      | ReplaceGlobalChatMessageAction
      | UpsertGlobalChatConversationAction
      | SetGlobalChatConversationLoadingAction
    >,
  ): Promise<SendGlobalChatMessageResult | undefined> => {
    const trimmed = text.trim();
    if (!trimmed) return undefined;

    const tempId =
      optimisticMessageId ??
      `temp_user_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    if (!optimisticMessageId) {
      dispatch({
        type: GLOBAL_CHAT_ACTION.ADD_MESSAGE,
        conversationId,
        message: {
          id: tempId,
          user_id: "",
          conversation_id: conversationId,
          from: "user",
          message: trimmed,
          referenced_memory_ids: [],
          created_at: new Date().toISOString(),
        },
      });
    }
    dispatch({
      type: GLOBAL_CHAT_ACTION.SET_CONVERSATION_LOADING,
      conversationId,
      isLoading: true,
    });

    const res = await fetchApi<SendGlobalChatMessageResult>(
      "/conversations/create_chat_message",
      "POST",
      {
        conversation_id: conversationId,
        message: trimmed,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    );

    dispatch({
      type: GLOBAL_CHAT_ACTION.UPSERT_CONVERSATION,
      conversation: res.conversation,
    });
    dispatch({
      type: GLOBAL_CHAT_ACTION.REPLACE_MESSAGE,
      conversationId,
      tempId,
      message: res.user_message,
    });
    dispatch({
      type: GLOBAL_CHAT_ACTION.ADD_MESSAGE,
      conversationId,
      message: res.ai_message,
    });
    dispatch({
      type: GLOBAL_CHAT_ACTION.SET_CONVERSATION_LOADING,
      conversationId,
      isLoading: false,
    });

    return res;
  };
};
